import React, { useMemo, useState, useEffect } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Navigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { relatorioService, unidadeService } from '../services/api';
import { usePermissaoRecurso } from '../hooks/usePermissoes';

const ORIGEM_LABELS = {
  RECEITA_MANUAL: 'Receita Manual',
  RECEITA_MATRICULA: 'Receita de Matrícula',
};

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(numeric);
};

const RelatoriosContabeis = () => {
  const { loading: permissaoLoading, isAdmin, podeVisualizar } = usePermissaoRecurso('relatorios');
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [buscou, setBuscou] = useState(false);
  const [unidades, setUnidades] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [itens, setItens] = useState([]);
  const [filtros, setFiltros] = useState({
    data_inicio: null,
    data_fim: null,
    tipo: 'ambos',
    origem: 'todos',
    id_unidade: null,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_items: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });

  useEffect(() => {
    const fetchUnidades = async () => {
      try {
        const response = await unidadeService.list();
        setUnidades(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Erro ao carregar unidades', error);
      }
    };

    fetchUnidades();
  }, []);

  const buscarRelatorio = async ({ page = 1, pageSize = 50, filtrosAtuais = filtros } = {}) => {
    if (!filtrosAtuais.data_inicio || !filtrosAtuais.data_fim) {
      message.warning('Informe data inicial e data final para buscar.');
      return;
    }

    setLoading(true);
    try {
      const params = {
        data_inicio: dayjs(filtrosAtuais.data_inicio).format('YYYY-MM-DD'),
        data_fim: dayjs(filtrosAtuais.data_fim).format('YYYY-MM-DD'),
        tipo: filtrosAtuais.tipo,
        origem: filtrosAtuais.origem,
        page,
        page_size: pageSize,
      };

      if (filtrosAtuais.id_unidade) {
        params.id_unidade = filtrosAtuais.id_unidade;
      }

      const response = await relatorioService.getRelatoriosContabeis(params);
      const payload = response.data || {};

      setResumo(payload.resumo || null);
      setItens(Array.isArray(payload.itens) ? payload.itens : []);
      setPagination(payload.pagination || {
        page,
        page_size: pageSize,
        total_items: 0,
        total_pages: 0,
        has_next: false,
        has_prev: page > 1,
      });
      setBuscou(true);
    } catch (error) {
      message.error('Erro ao carregar Relatórios Contábeis.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = () => {
    buscarRelatorio({ page: 1, pageSize: pagination.page_size });
  };

  const handleTableChange = (tablePagination) => {
    buscarRelatorio({
      page: tablePagination.current,
      pageSize: tablePagination.pageSize,
    });
  };

  const handleExportarExcel = async () => {
    if (!filtros.data_inicio || !filtros.data_fim) {
      message.warning('Informe data inicial e data final para exportar.');
      return;
    }

    setExportLoading(true);
    try {
      const dataInicio = dayjs(filtros.data_inicio).format('YYYY-MM-DD');
      const dataFim = dayjs(filtros.data_fim).format('YYYY-MM-DD');
      const params = {
        data_inicio: dataInicio,
        data_fim: dataFim,
        tipo: filtros.tipo,
        origem: filtros.origem,
      };

      if (filtros.id_unidade) {
        params.id_unidade = filtros.id_unidade;
      }

      const response = await relatorioService.exportRelatoriosContabeisExcel(params);
      const blob = new Blob([
        response.data,
      ], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-contabil_${dataInicio}_a_${dataFim}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success('Exportação Excel concluída.');
    } catch (error) {
      message.error('Erro ao exportar Excel de Relatórios Contábeis.');
      console.error(error);
    } finally {
      setExportLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Data',
        dataIndex: 'data',
        key: 'data',
        width: 120,
        render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '-'),
      },
      {
        title: 'Tipo',
        dataIndex: 'tipo',
        key: 'tipo',
        width: 110,
        render: (value) => (
          <Tag color={value === 'ENTRADA' ? 'green' : 'red'}>
            {value || '-'}
          </Tag>
        ),
      },
      {
        title: 'Origem',
        dataIndex: 'origem',
        key: 'origem',
        width: 160,
        // Fase 5B: so' RECEITA_MANUAL/RECEITA_MATRICULA ganham rotulo amigavel, para
        // deixar uma linha de matricula claramente identificada (sem mudar o filtro
        // macro "Receitas", que continua agrupando as duas). FATURA_ASAAS/DESPESA/
        // FOLHA_PAGAMENTO mantem o rotulo atual (texto bruto), sem redesenhar a tela.
        render: (value) => ORIGEM_LABELS[value] || value,
      },
      {
        title: 'Descrição',
        dataIndex: 'descricao',
        key: 'descricao',
        ellipsis: true,
      },
      {
        title: 'Pessoa/Fornecedor',
        dataIndex: 'pessoa_cliente_fornecedor',
        key: 'pessoa_cliente_fornecedor',
        ellipsis: true,
        render: (value) => value || '-',
      },
      {
        title: 'Competência',
        dataIndex: 'competencia',
        key: 'competencia',
        width: 120,
        render: (value) => value || '-',
      },
      {
        title: 'Unidade',
        dataIndex: 'unidade',
        key: 'unidade',
        width: 180,
        render: (value) => value?.nome || '-',
      },
      {
        title: 'Valor Líquido',
        dataIndex: 'valor_liquido',
        key: 'valor_liquido',
        width: 150,
        align: 'right',
        render: (value) => formatCurrency(value),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 140,
        render: (value) => <Tag>{value || '-'}</Tag>,
      },
    ],
    []
  );

  if (permissaoLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" tip="Validando permissões..." />
      </div>
    );
  }

  if (!isAdmin && !podeVisualizar) {
    return <Navigate to="/acesso-negado" replace />;
  }

  return (
    <div>
      <Card title="Relatórios Contábeis">
        <Space wrap style={{ marginBottom: 16 }}>
          <DatePicker
            placeholder="Data início"
            format="DD/MM/YYYY"
            value={filtros.data_inicio}
            onChange={(date) => setFiltros((prev) => ({ ...prev, data_inicio: date }))}
          />
          <DatePicker
            placeholder="Data fim"
            format="DD/MM/YYYY"
            value={filtros.data_fim}
            onChange={(date) => setFiltros((prev) => ({ ...prev, data_fim: date }))}
          />
          <Select
            value={filtros.tipo}
            style={{ width: 140 }}
            onChange={(value) => setFiltros((prev) => ({ ...prev, tipo: value }))}
            options={[
              { label: 'Ambos', value: 'ambos' },
              { label: 'Entradas', value: 'entradas' },
              { label: 'Saídas', value: 'saidas' },
            ]}
          />
          <Select
            value={filtros.origem}
            style={{ width: 150 }}
            onChange={(value) => setFiltros((prev) => ({ ...prev, origem: value }))}
            options={[
              { label: 'Todos', value: 'todos' },
              { label: 'Receitas', value: 'receitas' },
              { label: 'Faturas', value: 'faturas' },
              { label: 'Despesas', value: 'despesas' },
              { label: 'Folha', value: 'folha' },
            ]}
          />
          <Select
            allowClear
            placeholder="Todas as unidades"
            style={{ width: 220 }}
            value={filtros.id_unidade}
            onChange={(value) => setFiltros((prev) => ({ ...prev, id_unidade: value || null }))}
            options={unidades.map((unidade) => ({
              label: unidade.nome_fantasia,
              value: unidade.id,
            }))}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleBuscar}
            loading={loading}
          >
            Buscar
          </Button>
          <Button
            onClick={handleExportarExcel}
            loading={exportLoading}
            disabled={loading}
          >
            Exportar Excel
          </Button>
        </Space>

        <Divider style={{ marginTop: 0 }} />

        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Entradas"
                value={resumo?.entradas?.valor_liquido_total || 0}
                precision={2}
                prefix="R$"
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Saídas"
                value={resumo?.saidas?.valor_liquido_total || 0}
                precision={2}
                prefix="R$"
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="Saldo"
                value={resumo?.resultado?.saldo_liquido || 0}
                precision={2}
                prefix="R$"
                valueStyle={{
                  color: (resumo?.resultado?.saldo_liquido || 0) >= 0 ? '#3f8600' : '#cf1322',
                }}
              />
            </Card>
          </Col>
        </Row>

        <Table
          rowKey="id_origem"
          columns={columns}
          dataSource={itens}
          loading={loading}
          scroll={{ x: 1100 }}
          locale={{
            emptyText: buscou
              ? 'Nenhum lançamento encontrado para os filtros informados.'
              : 'Informe o período e clique em Buscar para consultar o relatório.',
          }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.page_size,
            total: pagination.total_items,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showTotal: (total) => `${total} registro(s)`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default RelatoriosContabeis;
